import { Question, Star } from 'phosphor-react'
import { useEffect, useState, useCallback } from 'react'
import {
  Col,
  Container,
  Dropdown,
  Modal,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap'
import { toast } from 'react-toastify'
import SearchInput from '../../../../components/SearchInput'
import { useLoading } from '../../../../hooks/LoadingContext'
import DashboardLayout from '../../../../layouts/Dashboard'
import api from '../../../../services/api'
import { errorApi } from '../../../../utils/errorApi'

import styles from './styles.module.css'
import { isSmallDevice } from '../../../../utils/deviceScreen'
import { createPortal } from 'react-dom'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import { DomainTuple } from 'victory'
import RatingHistoryChart from '../../../../components/RatingHistoryChart'

const MotoristasAvaliacoes: React.FC = () => {
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')
  const [rankings, setRankings] = useState<any>([])
  const [selectedRanking, setSelectedRanking] = useState<any>({})

  const [xZoomDomain, setXZoomDomain] = useState<any>([0, 15])
  const [ratingChartData, setRatingChartData] = useState<any>([])

  const [showModal, setShowModal] = useState(false)
  const [loadMore, setLoadMore] = useState(true)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [changingSelectedRanking, setChangingSelectedRanking] = useState(false)

  const { setIsLoading } = useLoading()

  const loadRankings = useCallback(async () => {
    setIsLoading(true)

    try {
      let apiUrl = `/company/drivers/rankings?name=${search}&limit=20`

      if (search && search !== lastSearch) {
        apiUrl += '&search=' + search
        setLastSearch(search)
      }
      const offset = rankings?.length || 0
      if (offset > 0) apiUrl += `&offset=${offset}`

      const { data } = await api.get(apiUrl)

      setRankings(data)
    } catch (err) {
      toast.error(errorApi(err, 'Erro ao carregar avaliações'))
    }
    setIsLoading(false)
  }, [rankings?.length, search, lastSearch, setIsLoading])

  useEffect(() => {
    loadRankings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadRatingHistory = useCallback(async () => {
    try {
      let apiUrl = `/company/requests?status=delivered&driver_id=${selectedRanking.driver_id}&has_client_rating=true&limit=20`

      const offset = ratingChartData?.length || 0
      if (offset > 0) apiUrl += `&offset=${offset}`

      const { data } = await api.get(apiUrl)

      let ratings = data.map((rating: any, index: number) => ({
        x: offset + index + 1,
        y: rating.customer_rating_stars,
      }))

      setHasMoreData(!!ratings.length)
      setRatingChartData([...ratingChartData, ...ratings])
    } catch (err) {
      toast.error(errorApi(err, 'Erro ao carregar histórico de avaliações'))
    }
    setLoadMore(false)
  }, [ratingChartData, selectedRanking.driver_id])

  useEffect(() => {
    if (!changingSelectedRanking) {
      if (!Object.keys(selectedRanking).length) return
      if (!loadMore || !hasMoreData) return
    }

    setChangingSelectedRanking(false)

    loadRatingHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRanking, loadMore, changingSelectedRanking, hasMoreData])

  const handleSearch = (value: string) => {
    setSearch(value)
    loadRankings()
  }

  const handleDomainChange = (domain: { x: any; y?: DomainTuple }) => {
    setXZoomDomain(domain.x)

    if (domain.x[1] > ratingChartData.length && !loadMore && hasMoreData) {
      setLoadMore(true)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setRatingChartData([])
    setLoadMore(true)
    setHasMoreData(true)
  }

  const handleOpenModal = (ranking: any) => {
    setRatingChartData([])
    setSelectedRanking(ranking)
    setShowModal(true)
  }

  return (
    <DashboardLayout pageName="Avaliações" backgroundColor="#F8F8F8">
      <LoadingSpinner text="Buscando avaliações" />
      <Container className="py-2 px-1 px-sm-2 px-md-5">
        <Row className="pl-1 pl-sm-2 pl-lg-5">
          <div style={{ marginLeft: -10 }}>
            <SearchInput
              placeholder="Digite o nome do motorista"
              setValue={(value: string) => setSearch(value)}
              handleSearch={handleSearch}
              showOptions={false}
              md={isSmallDevice() ? 12 : 10}
            />
          </div>
        </Row>
        <Row className="pt-4 h-100">
          {rankings?.length === 0 ? (
            <span className={`${styles.infoBadge} text-center`}>Nenhum motorista encontrado</span>
          ) : (
            <Table hover responsive className={styles.tableBorder}>
              <thead>
                <tr>
                  <th className={`${styles.tableHeader} ${styles.tableHeaderLeft}`}>Ordem</th>
                  <th className={styles.tableHeader}>Nome do motorista</th>
                  <th className={styles.tableHeader}>
                    Nota geral
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-icon" style={{ fontSize: 12, width: 'fit-content' }}>
                          (soma avaliações) / (nro. de entregas com avaliação)
                        </Tooltip>
                      }
                    >
                      <Question style={{ marginLeft: 5 }} size={20} color="#FFFFFF" />
                    </OverlayTrigger>
                  </th>
                  <th className={`${styles.tableHeader} ${styles.tableHeaderRight}`}></th>
                </tr>
              </thead>
              <tbody>
                {rankings?.map((ranking: any, index: number) => (
                  <tr
                    key={ranking.driver_id}
                    className={
                      styles.tableBody +
                      ' ' +
                      `${index === rankings.length - 1 ? styles.tableCellLast : ''}` +
                      `${index === 0 ? styles.tableCellFirst : ''}`
                    }
                  >
                    <td className={`${styles.tableCell} ${styles.tableCellLeft}`}>{index + 1}°</td>
                    <td className={styles.tableCell}>{ranking.driver_name}</td>
                    <td className={styles.tableCell}>{ranking.average_stars_rating.toFixed(2)}</td>
                    <td className={`${styles.tableCell} ${styles.tableCellRight}`}>
                      <Dropdown
                        drop="start"
                        onToggle={(isOpen) => {
                          setXZoomDomain([0, 15])
                          setChangingSelectedRanking(true)
                          if (isOpen) {
                            handleOpenModal(ranking)
                          } else {
                            handleCloseModal()
                          }
                        }}
                      >
                        <Dropdown.Toggle className={styles.ratingHistoryBtn} id="dropdown-basic">
                          Ver histórico
                        </Dropdown.Toggle>

                        {!isSmallDevice() ? (
                          <>
                            {createPortal(
                              <Dropdown.Menu
                                style={{
                                  width: 378,
                                  borderColor: '#053C5E',
                                  borderWidth: 1,
                                  borderRadius: '10px',
                                }}
                              >
                                <Container>
                                  <Row className="d-flex flex-row justify-content-center align-items-center">
                                    <Col xs={3} sm={3} md={3}>
                                      <Star size={70} weight="thin" color="#053C5E" />
                                    </Col>
                                    <Col xs={9} sm={9} md={9} className="p-0">
                                      <p className={styles.dropdownTitle}>Avaliação atual</p>
                                      <span className={styles.dropdownRating}>
                                        {selectedRanking?.average_stars_rating?.toFixed(2)}
                                      </span>
                                      <span className={styles.dropdownSum}>
                                        {`(${selectedRanking?.rated_requests} ${selectedRanking?.rated_requests > 1 ? 'avaliações' : 'avaliação'})`}
                                      </span>
                                    </Col>
                                  </Row>
                                  <Row style={{ overflow: 'hidden' }}>
                                    {ranking && ranking.driver_id > 0 && (
                                      <RatingHistoryChart
                                        ratingChartData={ratingChartData}
                                        handleDomainChange={handleDomainChange}
                                        xZoomDomain={xZoomDomain}
                                      />
                                    )}
                                  </Row>
                                </Container>
                              </Dropdown.Menu>,
                              document.body,
                            )}
                          </>
                        ) : (
                          <Modal
                            show={showModal && isSmallDevice()}
                            onHide={() => handleCloseModal()}
                            size="lg"
                            centered
                          >
                            <Modal.Header closeButton></Modal.Header>
                            <Modal.Body>
                              <Row className="d-flex flex-row align-items-center">
                                <Star
                                  size={70}
                                  weight="thin"
                                  color="#053C5E"
                                  style={{ maxWidth: 'fit-content' }}
                                />
                                <div className="p-0" style={{ maxWidth: 'fit-content' }}>
                                  <p className={styles.dropdownTitle}>Avaliação atual</p>
                                  <span className={styles.dropdownRating}>
                                    {selectedRanking?.average_stars_rating}
                                  </span>
                                  <span className={styles.dropdownSum}>
                                    {`(${selectedRanking?.rated_requests} ${selectedRanking?.rated_requests > 1 ? 'avaliações' : 'avaliação'})`}
                                  </span>
                                </div>
                              </Row>
                              <Row>
                                <RatingHistoryChart
                                  key={ranking.driver_id}
                                  ratingChartData={ratingChartData}
                                  handleDomainChange={handleDomainChange}
                                  xZoomDomain={xZoomDomain}
                                />
                              </Row>
                            </Modal.Body>
                          </Modal>
                        )}
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Row>
      </Container>
    </DashboardLayout>
  )
}

export default MotoristasAvaliacoes
